import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { get, post, patch } from "../../lib/request";
import { buildMonitoringScheduleFetchKey } from "./fetchCacheUtils";

/** Existing schedule from GET has an `id`; null/empty body means create via POST. */
export function getMonitoringScheduleId(schedule) {
  if (schedule == null || typeof schedule !== "object") return null
  const id = schedule.id
  if (id == null || id === "") return null
  return id
}

/* ================================
   GET Monitoring Schedule
================================ */

export const fetchMonitoringSchedule = createAsyncThunk(
  "monitoringSchedule/fetch",
  async ({ clientId, organizationId }, { rejectWithValue }) => {
    try {
      let query = "";

      if (clientId) {
        query = `?clientId=${encodeURIComponent(clientId)}`;
      } else if (organizationId) {
        query = `?orgId=${encodeURIComponent(organizationId)}`;
      } else {
        return rejectWithValue("clientId or organizationId required");
      }


      const res = await get(`monitoring-schedule${query}`);

      if (res == null) return null
      const body = res?.data !== undefined ? res.data : res
      if (body == null) return null
      if (typeof body === "object" && !getMonitoringScheduleId(body)) {
        return null
      }
      return body
    } catch (err) {
      const msg = String(err?.message || "")
      if (msg.includes("404") || msg.toLowerCase().includes("not found")) {
        return null
      }
      console.error("❌ fetchMonitoringSchedule error:", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      const key = buildMonitoringScheduleFetchKey(arg)
      if (!key) return false
      const s = getState().monitoringSchedule
      if (s.loading && s.lastFetchKey === key) return false
      if (!s.loading && s.lastFetchKey === key && s.status === "succeeded") {
        return false
      }
      return true
    },
  },
);


/* ================================
   POST Monitoring Schedule
================================ */

export const saveMonitoringSchedule = createAsyncThunk(
  "monitoringSchedule/save",
  async (payload, { rejectWithValue, getState }) => {
    try {
      const body = { ...payload }
      const explicitId = body.existingScheduleId
      delete body.existingScheduleId

      const existingId =
        explicitId != null && explicitId !== ""
          ? explicitId
          : getMonitoringScheduleId(getState()?.monitoringSchedule?.data)

      let res
      if (existingId != null && existingId !== "") {
        res = await patch(`monitoring-schedule/${existingId}`, body)
      } else {
        res = await post("monitoring-schedule", body)
      }

      const saved = res?.data !== undefined ? res.data : res
      return saved ?? body
    } catch (err) {
      console.error("❌ saveMonitoringSchedule error:", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const monitoringScheduleSlice = createSlice({
  name: "monitoringSchedule",
  initialState: {
    data: null,
    loading: false,
    saving: false,
    error: null,
    status: "idle",
    lastFetchKey: null,
  },
  reducers: {},

  extraReducers: (builder) => {
    builder

      /* ===== GET ===== */

      .addCase(fetchMonitoringSchedule.pending, (state, action) => {
        state.loading = true;
        state.status = "loading"
        state.error = null;
        state.lastFetchKey = buildMonitoringScheduleFetchKey(action.meta.arg)
      })

      .addCase(fetchMonitoringSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.status = "succeeded"
        state.data = action.payload;
      })

      .addCase(fetchMonitoringSchedule.rejected, (state, action) => {
        state.loading = false;
        state.status = "failed"
        state.error = action.payload;
      })

      /* ===== POST ===== */

      .addCase(saveMonitoringSchedule.pending, (state) => {
        state.saving = true;
        state.error = null;
      })

      .addCase(saveMonitoringSchedule.fulfilled, (state, action) => {
        state.saving = false;
        state.data = action.payload;
      })

      .addCase(saveMonitoringSchedule.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export default monitoringScheduleSlice.reducer;
